import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { shouldShowReturnableColumn } from 'app/common/constants/project-names.constants';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { Subject } from 'rxjs';
import { UnitMeasureDisplayComponent } from '../unit-measure-display/unit-measure-display.component';
import { BomComponent, BomModalData, BomTreeNode } from './bom-modal.types';

/**
 * BomModalComponent - Hierarchical BOM viewer with lazy loading
 * Displays nested BOM structures with expand/collapse functionality
 */
@Component({
  selector: 'app-bom-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTreeModule,
    MatProgressSpinnerModule,
    UnitMeasureDisplayComponent,
    TranslatePipe,
  ],
  templateUrl: './bom-modal.component.html',
  styleUrls: ['./bom-modal.component.scss'],
})
export class BomModalComponent implements OnInit, OnDestroy {
  private readonly i18n = inject(LanguageService);

  bomData: BomModalData;
  treeControl = new NestedTreeControl<BomTreeNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<BomTreeNode>();
  listComponents: BomComponent[] = [];
  readonly showReturnableColumn = shouldShowReturnableColumn();

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<BomModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BomModalData
  ) {
    this.bomData = data;
  }

  ngOnInit(): void {
    if (this.bomData.showAsTree) {
      this.buildTree();
    } else {
      this.buildList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildTree(): void {
    if (!this.bomData.showAsTree) {
      return;
    }

    const normalizedComponents = this.bomData.components.map((comp) =>
      this.normalizeComponent(comp)
    );

    const treeNodes = this.processComponents(normalizedComponents, 0);
    this.dataSource.data = treeNodes;
    this.treeControl.dataNodes = treeNodes;
  }

  private buildList(): void {
    this.listComponents = this.bomData.components.map((comp) => this.normalizeComponent(comp));
  }

  private normalizeComponent(data: any): BomComponent {
    return {
      _id: data._id,
      componentType: data.componentType || 'MATERIAL',
      material: data.material,
      BOM: data.BOM,
      materialBOM: data.materialBOM,
      soldQuantity: data.soldQuantity || 0,
      soldUnitOfMeasure: data.soldUnitOfMeasure || { code: 'N/A', name: 'N/A' },
      baseQuantity: data.baseQuantity || 0,
      baseUnitOfMeasure: data.baseUnitOfMeasure || { code: 'N/A', name: 'N/A' },
      pararellQuantity: data.pararellQuantity || 0,
      isReturnable: data.isReturnable || false,
      notes: data.notes,
    };
  }

  private processComponents(
    components: BomComponent[],
    level: number,
    maxDepth: number = 10
  ): BomTreeNode[] {
    if (!components || !Array.isArray(components)) {
      return [];
    }

    if (level >= maxDepth) {
      return [];
    }

    return components.map((component) => {
      const isNestedBom = component.componentType === 'BOM';

      const displayCode =
        component.material?.code || component.BOM?.code || component.materialBOM?.code || 'N/A';

      const displayName =
        component.material?.name ||
        component.BOM?.name ||
        component.materialBOM?.name ||
        this.i18n.t('BOM_MODAL.MESSAGES.NO_NAME');

      const children =
        isNestedBom && component.BOM?.components
          ? this.processComponents(component.BOM.components, level + 1, maxDepth)
          : undefined;

      return {
        ...component,
        level,
        displayCode,
        displayName,
        icon: isNestedBom ? 'folder' : 'inventory_2',
        hasNestedBom: isNestedBom && !!component.BOM,
        children,
      };
    }) as BomTreeNode[];
  }

  hasChild = (_: number, node: BomTreeNode): boolean => {
    return !!node.children;
  };

  isRootLevel(node: BomTreeNode): boolean {
    return node.level === 0;
  }

  getLevelColor(level: number): string {
    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];
    return colors[level % colors.length];
  }

  formatBoolean(value: boolean): string {
    return value ? this.i18n.t('BOM_MODAL.VALUES.YES') : this.i18n.t('BOM_MODAL.VALUES.NO');
  }

  componentTypeLabel(componentType: BomComponent['componentType']): string {
    return componentType === 'BOM'
      ? this.i18n.t('BOM_MODAL.LABELS.BOM')
      : this.i18n.t('BOM_MODAL.LABELS.MATERIAL');
  }

  toggleAriaLabel(displayName: string): string {
    return this.i18n.t('BOM_MODAL.ACTIONS.TOGGLE_NODE', { name: displayName });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
